import type { FC } from 'react';
import { useCallback } from 'react';
import type { ActionProps } from '../../types';
import { observer } from 'mobx-react-lite';
import useRulePageMode from '../../hooks/useRulePageMode';
import { useFormContext } from 'react-hook-form';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import type { Option, OptionValue } from '../../../../types/shared';
import { SUB_ACTIONS } from '../../constants';
import getActionLevelTooltip from '../../utils/getActionLevelTooltip';
import { InfoOutlined } from '@mui/icons-material';
import { RuleEditAutoCompleteStyled } from '../RuleEditTasksFormComponents';
import { facebookActionTypesOptions } from '../../api';
import { useSuspenseQuery } from '@tanstack/react-query';

export const SubActionsType: FC<Pick<ActionProps, 'indexAction' | 'indexTask' | 'indexVariant'>> = observer(
  ({ indexAction, indexVariant, indexTask }) => {
    const isVariant = typeof indexVariant === 'number';
    const { isRead } = useRulePageMode();
    const { watch } = useFormContext();
    const ruleLevel = watch('level') as string;
    const tasksFormStore = useCurrentTasksFormStore();
    const handleChangeMetric = useCallback(
      (value: OptionValue) => {
        tasksFormStore.setSubActionField({
          indexTask,
          indexVariant,
          indexAction,
          field: 'action',
          value: value as string,
        });
      },
      [tasksFormStore, indexTask, indexVariant, indexAction]
    );

    const storedAction = tasksFormStore.getActionValues({ indexTask, indexAction, indexVariant }).action;
    const storedSubAction = tasksFormStore.getSubActionValues({ indexTask, indexAction, indexVariant }).action;

    const { data: facebookActions } = useSuspenseQuery({
      ...facebookActionTypesOptions,
      select: (data): Option[] => {
        const subActions: string[] = storedAction ? (SUB_ACTIONS[String(storedAction)] ?? []) : [];
        return data
          .filter(({ name }) => subActions.includes(name) && storedAction !== name) // only sub actions value and exclude sub_budget action in case with a the same sub action value
          .map(({ name, level }) => {
            const disabled = level?.toLowerCase() !== ruleLevel?.toLowerCase() && level !== 'all';
            return {
              value: name,
              label: name,
              disabled,
              tooltip: disabled ? getActionLevelTooltip(level) : undefined,
              icon: disabled ? <InfoOutlined color={'inherit'} sx={{ fontSize: 21, p: 0, m: 0 }} /> : undefined,
            };
          });
      },
    });

    return (
      <RuleEditAutoCompleteStyled
        allSelectable={false}
        value={storedSubAction ?? ''}
        onChange={handleChangeMetric}
        id={`Autocomplete.Task:${indexTask}.Variant:${indexVariant}.Action:${indexAction}.SubActions:Action`}
        options={facebookActions}
        InputProps={{
          ...(tasksFormStore?.propsRestriction ?? null),
          disabled: isRead || isVariant,
        }}
      />
    );
  }
);

export default SubActionsType;
